import { useAuth } from "react-oidc-context";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import {
  ImapCard,
  JobPreferencesCard,
  LinkedInCard,
  type Config,
  type SecretStatus,
} from "../components/SettingsCards";

interface SettingsResponse {
  config: Config;
  secrets: { imap: SecretStatus; linkedin: SecretStatus; anthropic: SecretStatus };
}

export function Settings() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["settings"],
    queryFn: () => api<SettingsResponse>("/settings", token),
    enabled: !!token,
  });

  const saveConfig = useMutation({
    mutationFn: (c: Partial<Config>) =>
      api<{ ok: boolean }>("/settings", token, { method: "PUT", body: JSON.stringify(c) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings"] }),
  });

  const saveSecret = useMutation({
    mutationFn: (args: { name: string; payload: Record<string, string | number> }) =>
      api<{ ok: boolean; updated_at: string }>(`/settings/secret/${args.name}`, token, {
        method: "PUT",
        body: JSON.stringify(args.payload),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings"] }),
  });

  if (q.isPending) return <p className="text-slate-400">Loading…</p>;
  if (q.error) return <p className="text-rose-300">Error: {(q.error as Error).message}</p>;
  if (!q.data) return null;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-slate-50">Settings</h1>
        <p className="mt-1 text-sm text-slate-400">
          Credentials are write-only — once saved, the value is hidden. Update by entering a new one.
        </p>
      </header>

      <JobPreferencesCard
        config={q.data.config}
        onSave={(c) => saveConfig.mutate(c)}
        saving={saveConfig.isPending}
      />

      <ImapCard
        status={q.data.secrets.imap}
        onSave={(p) => saveSecret.mutate({ name: "imap", payload: p })}
        saving={saveSecret.isPending}
      />

      <LinkedInCard
        status={q.data.secrets.linkedin}
        onSave={(p) => saveSecret.mutate({ name: "linkedin", payload: p })}
        saving={saveSecret.isPending}
      />
    </div>
  );
}
